// One component per block on a profile page.
//
// Each reads the store for itself and takes the rest from the page's context, so
// a block can be moved, resized, renamed or removed without any other block
// knowing. Adding one is a component here and a row in the catalog.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../data/account.ts';
import { useApp } from '../../data/store.tsx';
import { useSession } from '../../data/session.tsx';
import { GroupInvitation } from '../components/GroupInvitation.tsx';
import { minutesOfMusic } from '../../engine/picks.ts';
import { fmtTime } from '../../engine/time.ts';
import { renderMarkdown } from '../markdown.tsx';
import { dayColor, stageColor } from '../theme.ts';
import type { PanelContext, WidgetId } from './types.ts';

const EMOJI = [
  '🤘', '🔥', '💜', '🦖', '🌿', '✨', '🎶', '⚡',
  '🍄', '🌀', '👽', '💀', '🏕️', '🕺', '💃', '🫶', '😈', '🌙',
];

function About({ ctx }: { ctx: PanelContext }) {
  if (!ctx.editing) {
    return ctx.page.aboutMd.trim() ? (
      renderMarkdown(ctx.page.aboutMd)
    ) : (
      <p className="muted">Nothing written here yet.</p>
    );
  }
  return (
    <>
      <div className="emojibar">
        {EMOJI.map((glyph) => (
          <button key={glyph} onClick={() => ctx.setAbout(`${ctx.page.aboutMd}${glyph}`)} title={`Add ${glyph}`}>
            {glyph}
          </button>
        ))}
      </div>
      <textarea
        className="bioarea"
        rows={8}
        value={ctx.page.aboutMd}
        placeholder="Your story. Markdown works: **bold**, *italic*, - lists, > quotes, [links](https://example.com)"
        onChange={(e) => ctx.setAbout(e.target.value)}
        onBlur={ctx.commit}
      />
    </>
  );
}

function Lineup({ ctx }: { ctx: PanelContext }) {
  const { derived } = useApp();
  const picks = derived.picksByDay.get(ctx.activeDay ?? '') ?? [];
  return (
    <div className="rowlist">
      {picks.map((pick) => {
        const artist = derived.artistById.get(pick.set.artistId);
        const stage = derived.stageById.get(pick.set.stageId);
        return (
          <div
            key={pick.setId}
            className="row"
            style={{ borderLeft: `4px solid ${stageColor(pick.set.stageId)}` }}
          >
            <span>
              <b>{artist?.name ?? 'Unknown'}</b>
              <span className="sub" style={{ display: 'block' }}>
                {fmtTime(pick.startTs)} · {stage?.name ?? ''}
              </span>
            </span>
          </div>
        );
      })}
      {picks.length === 0 && (
        <p className="muted">
          Nothing planned for that day yet. <Link to="/plan">Pick sets</Link>
        </p>
      )}
    </div>
  );
}

function Photos({ ctx }: { ctx: PanelContext }) {
  if (ctx.photos.length === 0) {
    return (
      <p className="muted">
        {ctx.user
          ? 'Nothing uploaded yet. A photo stays private until you share it.'
          : 'Add photos on this device or sign in to share them.'}
      </p>
    );
  }
  return (
    <div className={`photogrid shape-${ctx.page.photoShape}`}>
      {ctx.photos.map((photo) => (
        <img key={photo.id} src={photo.url} alt={photo.caption} loading="lazy" />
      ))}
    </div>
  );
}

function Crew() {
  const { state } = useApp();
  const { session } = useSession();
  return (
    <>
      {(session?.crews ?? []).map((crew) => <GroupInvitation key={crew.id} crew={crew} />)}
      <p className="muted"><Link to="/search#my-groups">Create or manage groups and invitations</Link></p>
      {state.workspace && <>
      <h3>In this device’s plan</h3>
      <div className="rowlist">
        {state.members.map((member) => (
          <div key={member.userId} className="row">
            <span>
              <span className="dot" style={{ background: member.color }} /> {member.displayName}
              <span className="sub" style={{ display: 'block' }}>{member.role}</span>
            </span>
            <Link to="/messages" className="small ghost">
              message
            </Link>
          </div>
        ))}
      </div>
      </>}
    </>
  );
}

function Links({ ctx }: { ctx: PanelContext }) {
  if (ctx.page.links.length === 0) return <p className="muted">No links yet.</p>;
  return (
    <div className="rowlist">
      {ctx.page.links.map((link) => (
        <a key={link.url} className="row" href={link.url} target="_blank" rel="noreferrer noopener">
          {link.label}
        </a>
      ))}
    </div>
  );
}

function Numbers({ ctx }: { ctx: PanelContext }) {
  return (
    <div className="profilestats">
      {ctx.stats.map((stat) => (
        <div key={stat.label}>
          <strong>{stat.n}</strong>
          <span>{stat.label}</span>
        </div>
      ))}
    </div>
  );
}

function Art({ ctx }: { ctx: PanelContext }) {
  if (!ctx.user) {
    return (
      <p className="muted">
        Sign in to post your artwork.
      </p>
    );
  }
  if (ctx.art.length === 0 && ctx.offers.length === 0) {
    return (
      <p className="muted">
        Nothing posted yet. <Link to="/art">Post a piece</Link> and it shows up here.
      </p>
    );
  }
  return (
    <>
      {ctx.art.length > 0 && (
        <div className={`photogrid shape-${ctx.page.photoShape}`}>
          {ctx.art.map((piece) => (
            <img key={piece.id} src={piece.url} alt={piece.title} loading="lazy" />
          ))}
        </div>
      )}

      {ctx.art.length > 0 && (
        <div className="rowlist" style={{ marginTop: 8 }}>
          {ctx.art.map((piece) => (
            <div key={piece.id} className="row">
              <span>
                {piece.title}
                <span className="sub" style={{ display: 'block' }}>
                  {piece.medium ? `${piece.medium} · ` : ''}
                  {piece.tags.length > 0 ? piece.tags.map((t) => `#${t}`).join(' ') : 'no tags'}
                  {piece.status === 'flagged' ? ' · flagged for review' : ''}
                  {piece.status === 'blocked' ? ' · withheld, visible only to you' : ''}
                </span>
              </span>
              <button
                className="small ghost"
                onClick={async () => {
                  const result = await api.deleteArt(piece.id);
                  ctx.setArtNote(result.ok ? 'Taken down.' : (result.error ?? 'That did not delete.'));
                  await ctx.reloadArt();
                }}
              >
                delete
              </button>
            </div>
          ))}
        </div>
      )}

      {ctx.offers.length > 0 && (
        <>
          <h3 style={{ marginBottom: 4 }}>Offered to the site</h3>
          <div className="rowlist">
            {ctx.offers.map((offer) => (
              <div key={offer.id} className="row">
                <span>
                  {offer.title}
                  <span className="sub" style={{ display: 'block' }}>
                    {offer.status === 'open'
                      ? 'waiting to be read'
                      : offer.status === 'accepted'
                        ? `in use${offer.usedWhere ? `: ${offer.usedWhere}` : ''}`
                        : offer.status}
                    {' · credited as '}
                    {offer.attribution}
                  </span>
                </span>
                {offer.status === 'open' && (
                  <button
                    className="small ghost"
                    onClick={async () => {
                      const result = await api.withdrawSubmission(offer.id);
                      ctx.setArtNote(
                        result.ok ? 'Offer withdrawn.' : (result.error ?? 'That did not withdraw.'),
                      );
                      await ctx.reloadArt();
                    }}
                  >
                    withdraw
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {ctx.artNote && (
        <p className="muted" role="status">
          {ctx.artNote}
        </p>
      )}
    </>
  );
}

// Counts down to the first set on the bill, then reports the weekend while it is
// running, then stops counting. The clock ticks once a minute, which is as fine
// as a number stated in hours needs.
function Countdown() {
  const { state, derived } = useApp();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  if (state.sets.length === 0) return <p className="muted">No bill is loaded yet.</p>;

  const first = Math.min(...state.sets.map((s) => s.startTs));
  const last = Math.max(...state.sets.map((s) => s.endTs));
  const picks = [...derived.picksByDay.values()].flat();

  if (now < first) {
    const left = first - now;
    const days = Math.floor(left / 86_400_000);
    const hours = Math.floor((left % 86_400_000) / 3_600_000);
    const minutes = Math.floor((left % 3_600_000) / 60_000);
    return (
      <>
        <div className="countclock">
          {days > 0 && (
            <span>
              <b>{days}</b>
              {days === 1 ? 'day' : 'days'}
            </span>
          )}
          <span>
            <b>{hours}</b>
            {hours === 1 ? 'hour' : 'hours'}
          </span>
          <span>
            <b>{minutes}</b>
            {minutes === 1 ? 'minute' : 'minutes'}
          </span>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          until the first set. {picks.length} in your plan so far.
        </p>
      </>
    );
  }

  if (now <= last) {
    const live = picks.find((p) => p.startTs <= now && p.endTs > now);
    const next = picks.filter((p) => p.startTs > now).sort((a, b) => a.startTs - b.startTs)[0];
    const nameOf = (setId: string) => {
      const set = derived.setById.get(setId);
      return (set && derived.artistById.get(set.artistId)?.name) ?? 'that set';
    };
    return (
      <>
        <p className="countnow">
          {live ? nameOf(live.setId) : 'Nothing of yours is on right now.'}
        </p>
        <p className="muted" style={{ margin: '0 0 10px' }}>
          {next ? `Next: ${nameOf(next.setId)} at ${fmtTime(next.startTs)}.` : 'Nothing else picked.'}
        </p>
        <Link to="/event" className="small ghost">
          Open event mode
        </Link>
      </>
    );
  }

  const music = minutesOfMusic(picks);
  return (
    <p className="muted" style={{ margin: 0 }}>
      That was the weekend: {picks.length} sets and {Math.round(music / 60)} hours of music.
    </p>
  );
}

// The acts they rated highest, with where each one plays. Ratings the system
// guessed are left out: this block is a statement about them.
function Wanted() {
  const { state, derived } = useApp();
  const navigate = useNavigate();

  const top = state.ratings
    .filter((r) => r.userId === state.userId && r.source === 'manual')
    .sort((a, b) => b.score - a.score)
    .slice(0, 6);

  if (top.length === 0) {
    return (
      <p className="muted">
        Nothing rated yet. <Link to="/lineup">Go through the lineup</Link> and this fills itself.
      </p>
    );
  }

  return (
    <div className="rowlist">
      {top.map((rating) => {
        const artist = derived.artistById.get(rating.artistId);
        const set = state.sets.find((s) => s.artistId === rating.artistId);
        const day = set && state.days.find((d) => d.id === set.dayId);
        const stage = set && derived.stageById.get(set.stageId);
        return (
          <button
            key={rating.artistId}
            type="button"
            className="row choicerow"
            onClick={() => set && navigate(`/plan?day=${encodeURIComponent(set.dayId)}`)}
          >
            <span>
              {artist?.name ?? 'That artist'}
              <span className="sub" style={{ display: 'block' }}>
                {set ? `${day?.label ?? ''} · ${stage?.name ?? ''} · ${fmtTime(set.startTs)}` : 'not on this bill'}
              </span>
            </span>
            <span className="badge good">{rating.score}</span>
          </button>
        );
      })}
    </div>
  );
}

// Hours of music per day, as bars against the longest of the three. The bar is
// the control: it opens the grid on that day.
function Weekend() {
  const { derived } = useApp();
  const navigate = useNavigate();

  const days = derived.dayContexts.map((ctx) => {
    const picks = derived.picksByDay.get(ctx.day.id) ?? [];
    return { id: ctx.day.id, label: ctx.day.label, minutes: minutesOfMusic(picks), sets: picks.length };
  });
  const peak = Math.max(60, ...days.map((d) => d.minutes));

  if (days.length === 0) return <p className="muted">No days are loaded.</p>;

  return (
    <div className="weekendbars">
      {days.map((day) => (
        <button
          key={day.id}
          type="button"
          className="weekendbar"
          style={{ ['--day' as string]: dayColor(day.id) }}
          onClick={() => navigate(`/plan?day=${encodeURIComponent(day.id)}`)}
          aria-label={`${day.label}: ${day.sets} ${day.sets === 1 ? 'set' : 'sets'}, ${Math.floor(day.minutes / 60)} hours ${day.minutes % 60} minutes`}
        >
          <span className="weekendlabel">{day.label}</span>
          <span className="weekendtrack">
            <span className="weekendfill" style={{ width: `${Math.round((day.minutes / peak) * 100)}%` }} />
          </span>
          <span className="weekendvalue">
            {Math.floor(day.minutes / 60)}h {day.minutes % 60}m
          </span>
        </button>
      ))}
    </div>
  );
}

export const PANELS: Record<WidgetId, (props: { ctx: PanelContext }) => React.ReactNode> = {
  about: About,
  lineup: Lineup,
  photos: Photos,
  crew: Crew,
  links: Links,
  stats: Numbers,
  art: Art,
  countdown: Countdown,
  wanted: Wanted,
  weekend: Weekend,
};
